"use client";

import Link from "next/link";
import { useState } from "react";
import { signIn } from "next-auth/react";
import { motion } from "motion/react";
import { Download, Mail, ArrowLeft, Loader2, Check, Sparkles } from "lucide-react";
import { SiGoogle } from "react-icons/si";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function handleGoogleSignIn() {
    setIsLoading(true);
    await signIn("google", { callbackUrl: "/dashboard" });
  }

  async function handleEmailSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    await signIn("resend", { email, callbackUrl: "/dashboard" });
    setEmailSent(true);
    setIsLoading(false);
  }

  const benefits = [
    "5GB free storage",
    "Archive 1 Instagram account",
    "Download stories anytime",
    "No credit card required",
  ];

  return (
    <main className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/10 via-dark-surface to-neon-pink/5" />
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-neon-purple/20 rounded-full blur-[150px] -z-10" />
      <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-neon-pink/15 rounded-full blur-[120px] -z-10" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg relative z-10"
      >
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="glass-card-elevated p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon-purple to-neon-pink flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold">
                Create your account
              </h1>
              <p className="text-white/50 text-sm">Start archiving for free</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-8">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-center gap-2 text-sm text-white/70"
              >
                <Check className="w-4 h-4 text-cyber-lime flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          {emailSent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-cyber-lime/20 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-cyber-lime" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                Check your email!
              </h2>
              <p className="text-white/60">
                We sent a magic link to <strong>{email}</strong>
              </p>
              <button
                onClick={() => setEmailSent(false)}
                className="mt-6 text-neon-purple hover:text-neon-pink transition-colors"
              >
                Use a different email
              </button>
            </motion.div>
          ) : (
            <>
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl bg-white text-dark-surface font-medium hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <SiGoogle className="w-5 h-5" />
                    Sign up with Google
                  </>
                )}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-dark-elevated text-white/40">
                    or use email
                  </span>
                </div>
              </div>

              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-white/70 mb-2"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="input-glass"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Continue with email
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-white/50">
                Already have an account?{" "}
                <Link
                  href="/login"
                  className="text-neon-purple hover:text-neon-pink transition-colors"
                >
                  Sign in
                </Link>
              </p>

              <p className="mt-4 text-center text-xs text-white/30">
                By signing up, you agree to our Terms of Service and Privacy
                Policy.
              </p>
            </>
          )}
        </div>
      </motion.div>
    </main>
  );
}
